import type { VaultContact, VaultContactIdentity } from '@shared/types';
import type { Identity } from '../store/authStore';
import { isFullCircleIdentity } from './identityAccess';

export function findContactForIdentityContext(
  contacts: VaultContact[],
  peerIdentityId: string,
  localIdentityId?: string
): VaultContact | null {
  const matches = contacts.filter((contact) =>
    contact.identities.some((identity) => identity.identityId === peerIdentityId)
  );
  if (!matches.length) return null;

  if (localIdentityId) {
    const directGuestMatch = matches.find((contact) => contact.directGuest?.guestIdentityId === localIdentityId);
    if (directGuestMatch) return directGuestMatch;
    const regularMatch = matches.find((contact) => contact.relationshipKind !== 'direct_guest');
    if (regularMatch) return regularMatch;
  }

  return matches[0] || null;
}

/**
 * Get the primary (first) identity of a contact
 * Useful when UI code expects a single identity
 */
export function getPrimaryIdentity(contact: VaultContact): VaultContactIdentity | null {
  return contact.identities[0] || null;
}

function getOriginFromUrl(serverUrl?: string): string | null {
  if (!serverUrl) return null;
  const raw = serverUrl.trim();
  if (!raw) return null;
  try {
    return new URL(raw).origin;
  } catch {
    try {
      return new URL(`https://${raw}`).origin;
    } catch {
      return null;
    }
  }
}

export function getLocalIdentityIdForContactIdentity(
  contactIdentity: VaultContactIdentity | null | undefined,
  identities: Record<string, Identity>,
  preferredLocalIdentityId?: string
): string | null {
  if (!contactIdentity) return null;
  const matches: Identity[] = [];
  const serverId = (contactIdentity.serverId || '').trim();
  if (serverId) {
    matches.push(...Object.values(identities).filter((identity) => (identity.serverId || '').trim() === serverId));
  }

  const origin = getOriginFromUrl(contactIdentity.serverUrl);
  if (origin && matches.length === 0) {
    matches.push(...Object.values(identities).filter((identity) => getOriginFromUrl(identity.serverUrl) === origin));
  }

  const preferred = preferredLocalIdentityId ? identities[preferredLocalIdentityId] : undefined;
  if (preferred && matches.some((identity) => identity.identityId === preferred.identityId)) {
    return preferred.identityId;
  }
  return (matches.find(isFullCircleIdentity) || matches[0])?.identityId || null;
}

export function getReachableIdentity(
  contact: VaultContact,
  identities: Record<string, Identity>
): VaultContactIdentity | null {
  const preferredLocalIdentityId = contact.directGuest?.guestIdentityId;
  const primaryIdentity = getPrimaryIdentity(contact);
  if (getLocalIdentityIdForContactIdentity(primaryIdentity, identities, preferredLocalIdentityId)) {
    return primaryIdentity;
  }

  return contact.identities.find((identity) => Boolean(getLocalIdentityIdForContactIdentity(identity, identities, preferredLocalIdentityId))) || null;
}

export function moveIdentityToPrimary(
  contact: VaultContact,
  identityId: string
): VaultContactIdentity[] {
  const normalizedIdentityId = (identityId || '').trim();
  if (!normalizedIdentityId) return contact.identities;
  const selected = contact.identities.find((identity) => identity.identityId === normalizedIdentityId);
  if (!selected) return contact.identities;
  return [
    selected,
    ...contact.identities.filter((identity) => identity.identityId !== normalizedIdentityId)
  ];
}
